// « Moi » (D-100, D-101 ; maquette m3) : la même page pour tous — mes badges en tête, puis mes avantages, mes
// indisponibilités, les notifications, mon compte, l'aide. D-132 : Moi ne garde que le personnel ; « Inviter » et
// « Trouver un partenaire » sont dans Club › Aider le club. Un compte partenaire seul : au vouvoiement, sans ce qui
// ne le concerne pas (il garde « Trouver un partenaire », son espace n'a pas d'onglet Club des membres). « Mon compte » (#/moi/compte) : prénom, e-mail, dossier
// de licence, se déconnecter. « Enfants » (D-036) : pour un parent, chaque enfant — son équipe, son prochain
// rendez-vous, son dossier de licence, son code perso de connexion, et « Inviter l'autre parent » (D-103 :
// un lien parent d'un seul usage, 7 jours, sans validation ; le bureau voit qui est relié).
import { renderKidMv } from './mains-vives';
import { renderKidFormation } from './formation';
import { APPEARANCE_LABEL, readAppearance, setAppearance, type Appearance } from './appearance';
import { licencesOf, perksForMe, personAccess, saveProfileName, type LicenceRow, type PerkForMe } from '../lib/api';
import { displayName, agree } from '../domain/labels';
import { signOut } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { children, firstName, isMinorAccount, me, modOn, personaOf, refreshContext, teamOf, type Ctx, genderOf } from './context';
import { renderBirthdayRow } from './group-life';
import { loadAgenda, relativeDay } from './agenda';
import { renderLicenceCard } from './licences';
import { time } from './format';
import { $, $$, html, mount, openSheet, toast, armed } from './dom';
import { loadBadges, renderBadgesMoiCard, rewardsToUse } from './badges';
import { renderSupportRow } from './help';
import { NOTIF_WORDS, notifRole } from './notifs';
import { vouvoie } from './vous';
import { parseLocalDate } from '../domain/schedule';
import { kidDossier } from '../domain/licences';
import { guardianInviteLink } from '../lib/reach-api';
import { linksFor } from '../lib/club-links';
import { openShare } from './share';
import { followedNames, openAskSupporter } from './suivre';
import { renderTrips } from './recus';
import { renderMeEntries } from './recus-entries';
import { skinsInMoi } from './skin-owner';
import { showSkin, shownSkin } from './skin-boot';

const day = (d: string) => parseLocalDate(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
const row = (href: string, icon: string, title: string, sub: string, guide = '') => html`
  <a class="card card-row" href="${href}" ${guide ? html`data-guide="${guide}"` : ''}>
    <span class="mi-ico" aria-hidden="true"><span class="ti ${icon}"></span></span>
    <span class="card-main"><strong>${title}</strong><span class="muted small">${sub}</span></span>
    <span class="occ-go" aria-hidden="true">›</span>
  </a>`;

export async function renderMe(root: HTMLElement, ctx: Ctx, onChange: () => void, sub?: string): Promise<void> {
  if (sub === 'compte') return renderAccount(root, ctx, onChange);
  if (sub === 'trajets') return renderTrips(root, ctx);   // Mes trajets pour le club (D-124)
  if (sub === 'skins') return (await import('./skins')).renderMySkins(root, ctx, onChange);   // Mes skins (D-149)
  if (personaOf(ctx) === 'abonne') return renderFollowerMe(root, ctx);
  const minor = isMinorAccount(ctx);
  // Un compte partenaire seul (D-037) : vouvoiement ; ni indisponibilités, ni invitations, ni avantages des membres.
  const partnerOnly = !!ctx.partners.length && !ctx.full.people.length && !ctx.full.adminOf.length && !ctx.full.coachOf.size
    && !ctx.roles.some(r => r.role === 'supporter');
  const vous = vouvoie(ctx);
  const onTeam = ctx.full.people.some(p => ctx.full.memberships.some(m => m.person_id === p.id));
  // D-169 : mes accords (photos ; transport pour un mineur, en lecture), si je joue dans une équipe.
  const selfOnTeam = partnerOnly ? null : ctx.people.find(p => p.user_id === ctx.uid && ctx.memberships.some(m => m.person_id === p.id)) ?? null;
  // D-122 : rien d'un module éteint (badges et invitations de bénévoles, avantages, suivis, partenaires).
  const on = { vol: modOn(ctx, 'benevolat'), av: modOn(ctx, 'avantages'), page: modOn(ctx, 'page'), part: modOn(ctx, 'partenaires') };
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">Moi</p><h2>${firstName(ctx) || 'Moi'}</h2></header>
      ${on.vol ? html`<div id="me-badges"></div>` : ''}
      ${partnerOnly || !on.av ? '' : html`<a class="card card-row" href="#/avantages" id="me-perks" data-guide="me-perks">
        <span class="mi-ico" aria-hidden="true"><span class="ti ti-gift"></span></span>
        <span class="card-main"><strong>${vous ? 'Vos avantages' : 'Mes avantages'}</strong><span class="muted small" id="me-perks-sub">Récompenses des grades, offres du club</span></span>
        <span class="occ-go" aria-hidden="true" id="me-perks-n">›</span></a>`}
      ${partnerOnly || !on.page ? '' : followRow(vous)}
      ${partnerOnly ? '' : html`<div id="me-recus" class="screen"></div>`}
      ${onTeam && !partnerOnly ? row('#/indispos', 'ti-off', ((k: typeof ctx.people) => !k.length ? 'Mes indisponibilités' : k.length < ctx.people.length ? 'Indisponibilités (moi et mes enfants)' : k.length === 1 ? `Indisponibilités de ${displayName(k[0]!)}` : 'Indisponibilités de mes enfants')(ctx.people.filter(p => p.user_id !== ctx.uid)), 'Blessure, vacances, examens : le coach le sait d’avance') : ''}
      ${onTeam && !partnerOnly ? html`<div id="me-bday-row"></div>` : ''}
      ${selfOnTeam ? html`<a class="card card-row" href="#/accords/${selfOnTeam.id}" data-guide="me-accords">
        <span class="mi-ico" aria-hidden="true"><span class="ti ti-shield"></span></span>
        <span class="card-main"><strong>Mes accords</strong><span class="muted small">${minor ? 'Les photos et le transport : ce que tes parents ont choisi' : 'Les photos du club : oui, seulement dans l’app, ou non'}</span></span>
        <span class="occ-go" aria-hidden="true">›</span></a>` : ''}
      ${row('#/notifs', 'ti-bell', 'Notifications', NOTIF_WORDS[notifRole(ctx)].row)}
      <button type="button" class="card card-row me-look" id="me-look">
        <span class="mi-ico" aria-hidden="true"><span class="ti ti-palette"></span></span>
        <span class="card-main"><strong>Apparence</strong><span class="muted small" id="me-look-sub">${APPEARANCE_LABEL[readAppearance()]}${readAppearance() === 'auto' ? ' · comme le téléphone' : ''}</span></span>
        <span class="occ-go" aria-hidden="true" id="me-look-go">›</span></button>
      ${partnerOnly && on.part ? row('#/pistes', 'ti-handshake', 'Trouver un partenaire', vous ? 'Une entreprise que vous connaissez ?' : 'Une entreprise que tu connais ?') : ''}
      ${row('#/moi/compte', 'ti-user', 'Mon compte', minor ? 'Prénom, se déconnecter' : 'Prénom, e-mail, se déconnecter')}
      ${row('#/aide', 'ti-help', 'Aide et support', 'Tutos, « Montre-moi », écrire au support')}
      <div id="me-support"></div>
    </section>`);
  void renderSupportRow($(root, '#me-support')!, ctx);
  const bd = $(root, '#me-bday-row');
  if (bd) void renderBirthdayRow(bd, ctx).then(() => { if (!bd.childElementCount) bd.remove(); });   // D-151 : afficher ou masquer son anniversaire
  // Les skins vivent dans Apparence (D-157, discrets) : un nouveau skin gagné, seulement une pastille sur cette ligne.
  const skins = skinsInMoi(ctx);
  if (skins) void import('./skins').then(async m => {
    const n = await m.skinsUnseen(ctx);
    const go = $(root, '#me-look-go');
    if (n && go) go.outerHTML = `<span class="sk-tag new sk-new" id="me-look-go" aria-label="${n > 1 ? `${n} nouveaux skins` : 'Un nouveau skin'}">${n > 1 ? `${n} nouveaux` : 'Nouveau'}</span>`;
  }).catch(() => undefined);
  // Apparence : Automatique (le téléphone), Clair, Sombre ; retenue par l'appareil ; et « Mes skins » (D-157).
  $(root, '#me-look')?.addEventListener('click', () => {
    const cur = readAppearance();
    openSheet('Apparence', html`
      <div class="form">
        ${(['auto', 'light', 'dark'] as const).map(k => html`<label class="check me-look-opt"><input type="radio" name="look" value="${k}" ${cur === k ? 'checked' : ''}>
          <span><strong>${APPEARANCE_LABEL[k]}</strong>${k === 'auto' ? html`<br><span class="muted small">Comme le réglage du téléphone, clair le jour et sombre le soir s’il le fait.</span>` : ''}</span></label>`)}
        ${shownSkin() ? html`<p class="hint">Avec un skin, c’est le skin qui choisit clair ou sombre ; ce réglage vaut pour les couleurs du club.</p>` : ''}
        ${skins ? html`<div id="me-look-skins"></div>` : ''}
      </div>`, (sheet, close) => {
      const sk = $(sheet, '#me-look-skins');
      if (sk) void import('./skins').then(m => m.renderLookSkins(sk, ctx, close)).catch(() => sk.remove());
      for (const r of Array.from(sheet.querySelectorAll<HTMLInputElement>('input[name="look"]'))) r.addEventListener('change', () => {
        const k = r.value as Appearance;
        setAppearance(k);
        if (shownSkin()) showSkin(ctx.club?.id ?? null, true);   // le skin porté garde son clair ou son sombre (D-149)
        const sub = $(root, '#me-look-sub');
        if (sub) sub.textContent = `${APPEARANCE_LABEL[k]}${k === 'auto' ? ' · comme le téléphone' : ''}`;
        close();
      });
    });
  });
  if (on.page) void fillFollowRow(root, ctx);
  const rx = $(root, '#me-recus');
  if (rx) void renderMeEntries(rx, ctx).then(() => { if (!rx.childElementCount) rx.remove(); });   // trajets, signature (D-124)
  // Mes badges, et ce que j'ai à utiliser maintenant (avantages du club disponibles, récompenses des grades).
  const [d, perks] = await Promise.all([on.vol || on.av ? loadBadges(ctx).catch(() => null) : Promise.resolve(null),
    partnerOnly || !on.av ? Promise.resolve([] as PerkForMe[]) : perksForMe(ctx.club?.id ?? '').catch(() => [] as PerkForMe[])]);
  if (on.vol) void renderBadgesMoiCard($(root, '#me-badges')!, ctx, d);
  if (partnerOnly || !on.av) return;
  const now = perks.filter(p => !p.block && !p.partner_name && !p.min_level && !p.cost_points
    && (!p.person_id || ctx.people.find(x => x.id === p.person_id)?.user_id === ctx.uid));
  const titles = [...now.map(p => p.title), ...(d ? rewardsToUse(d).map(r => r.reward.title) : [])];
  const subEl = $(root, '#me-perks-sub'), nEl = $(root, '#me-perks-n');
  if (titles.length && subEl && nEl) {
    subEl.textContent = titles.slice(0, 2).map((t, i) => (i ? t.charAt(0).toLowerCase() + t.slice(1) : t)).join(', ') + (titles.length > 2 ? '…' : '');
    nEl.outerHTML = `<span class="chip chip-hot" aria-label="${titles.length} à utiliser">${titles.length}</span>`;
  }
}

/** « Mes suivis » (D-110) : les groupes que je suis, ceux de mes enfants d'office. */
const followRow = (vous: boolean) => html`<a class="card card-row fw-merow" href="#/suivis" id="me-follow" data-guide="me-follow">
  <span class="mi-ico" aria-hidden="true"><span class="fw-mi">★</span></span>
  <span class="card-main"><strong>${vous ? 'Vos suivis' : 'Mes suivis'}</strong><span class="muted small" id="me-follow-sub">${vous ? 'Les résultats des équipes que vous suivez' : 'Les résultats des équipes que tu suis'}</span></span>
  <span class="occ-go" aria-hidden="true">›</span></a>`;
async function fillFollowRow(root: HTMLElement, ctx: Ctx): Promise<void> {
  const el = root.querySelector<HTMLElement>('#me-follow-sub');
  if (!el || !ctx.club) return;
  const names = await followedNames(ctx.club.id);
  if (names.length) el.textContent = names.slice(0, 3).join(', ') + (names.length > 3 ? '…' : '');
}

/** Moi de l'abonné (D-110) : ses suivis, les notifications, « Rejoindre le club », son compte, l'aide ; rien de la vie interne. */
async function renderFollowerMe(root: HTMLElement, ctx: Ctx): Promise<void> {
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">Moi · abonné</p><h2>${firstName(ctx) || 'Moi'}</h2></header>
      ${row('#/notifs', 'ti-bell', 'Notifications', NOTIF_WORDS.abonne.row)}
      <button type="button" class="card card-row fw-rowbtn" id="me-supporter" data-guide="me-supporter">
        <span class="mi-ico" aria-hidden="true"><span class="ti ti-heart"></span></span>
        <span class="card-main"><strong>Rejoindre le club</strong><span class="muted small">Le bureau t’accueille : la vie du club, devenir bénévole</span></span>
        <span class="occ-go" aria-hidden="true">›</span></button>
      ${row('#/moi/compte', 'ti-user', 'Mon compte', 'Prénom, e-mail, se déconnecter')}
      ${row('#/aide', 'ti-help', 'Aide et support', 'Tutos, écrire au support')}
    </section>`);
  $(root, '#me-supporter')!.addEventListener('click', () => openAskSupporter(ctx));
}

/** « Mon compte » : le prénom, l'adresse de connexion, le dossier de licence, se déconnecter. */
async function renderAccount(root: HTMLElement, ctx: Ctx, onChange: () => void): Promise<void> {
  const self = me(ctx);
  const minor = isMinorAccount(ctx);
  const email = (await supabase?.auth.getSession())?.data.session?.user.email ?? '';
  const vous = vouvoie(ctx);
  mount(root, html`
    <section class="screen">
      <a class="back" href="#/moi">‹ Moi</a>
      <header class="screen-head"><p class="eyebrow">Moi</p><h2>Mon compte</h2></header>
      <section class="block">
        <h3>Prénom</h3>
        ${self ? html`<p class="muted small">Ta fiche au club : <strong>${displayName(self)}</strong> (modifiable par ton coach).</p>` : ''}
        <form class="inline-form" id="f-name" novalidate>
          <input name="name" maxlength="60" value="${ctx.profileName ?? (self ? displayName(self) : '')}" placeholder="${vous ? 'Votre prénom' : 'Ton prénom'}" aria-label="${vous ? 'Votre prénom' : 'Ton prénom'}" autocomplete="given-name">
          <button class="btn btn-ghost btn-sm" type="submit">Enregistrer</button>
        </form>
        <p class="hint">${vous ? 'C’est le prénom qui vous accueille dans l’app.' : 'C’est le prénom qui t’accueille dans l’app et qui s’affiche quand tu prends un créneau de bénévole.'}</p>
      </section>
      ${email && !email.endsWith('.arceau.app') ? html`<section class="block"><h3>Adresse de connexion</h3><p class="muted">${email}</p>
        <p class="hint">${vous ? 'Vous recevez un code à cette adresse pour vous connecter.' : 'Tu reçois un code à cette adresse pour te connecter.'}</p></section>` : ''}
      ${minor || !modOn(ctx, 'licences') ? '' : html`<div id="me-lic"></div>`}
      <button type="button" class="btn btn-ghost" id="b-out">Se déconnecter</button>
    </section>`);
  const lic = $(root, '#me-lic');
  if (lic) void renderLicenceCard(lic, { ...ctx, people: self ? [self] : [] });
  const f = $<HTMLFormElement>(root, '#f-name')!;
  f.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = String(new FormData(f).get('name') ?? '').trim();
    if (!name) { toast('Indique ton prénom.', 'ko'); return; }
    try { await saveProfileName(name); await refreshContext(ctx); toast('Prénom enregistré'); onChange(); }
    catch { toast('Enregistrement impossible.', 'ko'); }
  });
  $(root, '#b-out')!.addEventListener('click', () => void signOut());
}

export async function renderKids(root: HTMLElement, ctx: Ctx): Promise<void> {
  const kids = children(ctx);
  if (!kids.length) { mount(root, html`<div class="empty"><p class="empty-title">Aucun enfant rattaché à ton compte.</p></div>`); return; }
  const [{ today, items }, dossiers] = await Promise.all([
    loadAgenda(ctx, 21), modOn(ctx, 'licences') ? licencesOf(kids.map(k => k.id)).catch(() => [] as LicenceRow[]) : Promise.resolve([] as LicenceRow[]),
  ]);
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">Parent</p><h2>Mes enfants</h2></header>
      ${kids.map(k => {
        const kidTeams = ctx.memberships.filter(m => m.person_id === k.id).map(m => teamOf(ctx, m.team_id)).filter(t => !!t);
        const teams = kidTeams.map(t => t.name);
        const next = items.find(it => it.mine.some(m => m.personId === k.id));
        const presence = next?.mine.find(m => m.personId === k.id)?.presence;
        // Le dossier de la saison de ses équipes ; sans dossier, pas de ligne (le bureau voit « Dossier à ouvrir »).
        const dossier = kidDossier(dossiers, k.id, kidTeams.map(t => t.season_id).filter((s): s is string => !!s));
        return html`
        <section class="card kid">
          <div class="kid-head">
            <span class="kid-av" aria-hidden="true">${displayName(k).charAt(0).toUpperCase()}</span>
            <span><strong>${displayName(k)}</strong><span class="muted small">${teams.join(' · ') || 'Pas encore d’équipe'}</span></span>
          </div>
          ${next ? html`<a class="kid-row" href="#/e/${next.ev.id}/${next.occ.date}">
            <span class="eyebrow">Prochain · ${relativeDay(next.occ.date, today)}</span>
            <span><strong>${next.ev.title}</strong> · ${day(next.occ.date)} ${time(next.occ.start)}${next.ev.location ? ` · ${next.ev.location}` : ''}</span>
            <span class="small ${presence === 'absent' || presence === 'unavailable' ? 'ce-warn' : presence === 'uncertain' ? 'ix-unc' : 'ce-ok'}">${presence === 'absent' || presence === 'unavailable' ? agree(genderOf(ctx, k.id), 'Absent ou indisponible', 'Absente ou indisponible') : presence === 'uncertain' ? agree(genderOf(ctx, k.id), 'Incertain', 'Incertaine') : agree(genderOf(ctx, k.id), 'Attendu', 'Attendue')}</span>
          </a>` : html`<p class="muted small">Rien de prévu dans les 3 semaines.</p>`}
          ${k.user_id ? html`<div data-fo-kid="${k.id}"></div>` : ''}
          <div data-mv-kid="${k.id}"></div>
          <div data-ak-kid="${k.id}"></div>
          ${dossier.state === 'none' ? '' : html`<p class="small kid-lic">${dossier.state === 'missing' ? html`<span class="ce-warn">Dossier de licence : il manque ${dossier.missing.join(', ')}.</span>` : 'Dossier de licence : rien ne manque.'}</p>`}
          <div class="btn-row">
            <a class="btn btn-ghost btn-sm" href="#/indispos">Indisponibilités</a>
            ${k.login_handle ? html`<span class="muted small">Code perso : <span class="mono">${k.login_handle}</span></span>` : ''}
            <button type="button" class="btn btn-ghost btn-sm" data-guide="kid-code" data-code="${k.id}">${k.login_handle ? 'Nouveau code' : 'Créer son code perso'}</button>
            <button type="button" class="btn btn-ghost btn-sm" data-guide="kid-parent" data-other="${k.id}">Inviter l’autre parent</button>
          </div>
          <div class="code-out" data-out="${k.id}"></div>
        </section>`;
      })}
      <p class="hint">Le code perso permet à ton enfant d'ouvrir l'app sans e-mail. Il ne s'affiche qu'une fois : note-le.</p>
    </section>`);
  // D-141 : la formation d'un enfant mineur (f10), seulement si elle a commencé.
  for (const k of kids) { const el = $(root, `[data-fo-kid="${k.id}"]`); if (el) void renderKidFormation(el, ctx, k); }
  // D-164 : « Mains vives » d'un enfant mineur, là où le jeu est ouvert (la séance passe par le téléphone du parent).
  for (const k of kids) { const el = $(root, `[data-mv-kid="${k.id}"]`); if (el) void renderKidMv(el, ctx, k); }
  // D-169 : ses accords (photos, transport) et ses documents signés, avec « Modifier » et la preuve.
  void import('./accords').then(m => { for (const k of kids) { const el = $(root, `[data-ak-kid="${k.id}"]`); if (el) void m.renderKidAccords(el, ctx, k.id); } }).catch(() => undefined);
  for (const b of $$<HTMLButtonElement>(root, '[data-other]')) b.addEventListener('click', () => void inviteOtherParent(ctx, b.dataset.other!));
  for (const b of $$<HTMLButtonElement>(root, '[data-code]')) {
    b.addEventListener('click', async () => {
      if (b.textContent?.includes('Nouveau') && !armed(b, 'Confirmer : l’ancien code ne marchera plus')) return;
      const res = await personAccess('issue', b.dataset.code!);
      if (!res.handle || !res.code) { toast('Code impossible pour le moment.', 'ko'); return; }
      mount($(root, `[data-out="${b.dataset.code}"]`)!, html`
        <div class="code-card">
          <div><span class="muted small">Identifiant</span><strong class="mono">${res.handle}</strong></div>
          <div><span class="muted small">Code</span><strong class="mono code-big">${res.code.slice(0, 3)} ${res.code.slice(3)}</strong></div>
        </div>
        <p class="warn">Note-le maintenant : il ne sera plus jamais affiché.</p>`);
    });
  }
}

/** « Inviter l'autre parent » : un lien parent pour CET enfant, à envoyer en message privé. */
async function inviteOtherParent(ctx: Ctx, kidId: string): Promise<void> {
  const k = children(ctx).find(x => x.id === kidId);
  if (!k) return;
  const name = displayName(k);
  const club = ctx.clubs.find(c => c.id === k.club_id)?.name ?? ctx.club?.name ?? 'club';
  try {
    const [l, links] = await Promise.all([guardianInviteLink(k.id), linksFor(k.club_id)]);
    const until = new Date(l.expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
    openShare(`Inviter l’autre parent de ${name}`,
      `À envoyer à l’autre parent, en message privé : il ouvre le lien, donne son e-mail, reçoit un code, et suit ${name} lui aussi. Pas de validation ; le bureau voit qui est relié.`,
      `Voici ton accès à l’app du ${club} pour suivre ${name} : ${links.invite(l.token)} — tu donnes ton e-mail, tu reçois un code. Le lien ne sert qu’une fois, jusqu’au ${until}.`,
      { footer: html`<p class="hint">Un seul usage, valable jusqu’au ${until}.${l.opened_at ? ' Déjà ouvert.' : ''}</p>` });
  } catch { toast('Lien impossible pour le moment.', 'ko'); }
}

/** Accueil : demander le prénom une fois, aux comptes qui n'en ont pas (parents, coachs, bureau). */
export async function renderNamePrompt(el: HTMLElement, ctx: Ctx, onSaved: () => void): Promise<void> {
  if (firstName(ctx)) { mount(el, html``); return; }
  mount(el, html`
    <section class="card name-prompt">
      <p class="eyebrow">Bienvenue</p>
      <p class="card-title">Comment tu t'appelles ?</p>
      <form class="inline-form" id="f-first" novalidate>
        <input name="name" maxlength="60" placeholder="Ton prénom" aria-label="Ton prénom" autocomplete="given-name">
        <button class="btn btn-primary btn-sm" type="submit">OK</button>
      </form>
      <p class="hint">Pour t'accueillir par ton prénom et le montrer quand tu prends un créneau.</p>
    </section>`);
  const f = $<HTMLFormElement>(el, '#f-first')!;
  f.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = String(new FormData(f).get('name') ?? '').trim();
    if (!name) return;
    try { await saveProfileName(name); await refreshContext(ctx); onSaved(); }
    catch { toast('Enregistrement impossible.', 'ko'); }
  });
}
